import { resolveBookingCommercial } from "@/lib/bookingCommercial";
import type { ApiBooking, ApiPortInfo } from "@/types/api";
import { matchedBookingPrice, type MockBooking, type PortInfo } from "@/types/booking";

function toPortInfo(port: ApiPortInfo): PortInfo {
  return { locode: port.locode, name: port.name ?? port.locode };
}

function bookingRefFromApi(
  raw: string | null | undefined,
  sourceId: string,
): string | undefined {
  const ref = raw?.trim();
  if (!ref || ref === sourceId.trim()) return undefined;
  return ref;
}

function openedByFromExtra(
  extra: Record<string, unknown> | null | undefined,
): string | undefined {
  const raw = extra?.opened_by;
  return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

/**
 * Adapt an API booking into the presentational row view model the v2 booking
 * table and detail components already consume.
 */
export function apiBookingToRow(b: ApiBooking): MockBooking {
  const commercial = resolveBookingCommercial(b);
  return {
    id: b.id,
    source_id: b.source_id,
    file_id: b.file_id ?? undefined,
    state: b.state,
    tags: b.tags ?? [],
    flags: b.flags,
    carrier: commercial.carrier,
    contract_ref: commercial.contract_ref,
    named_account: commercial.named_account,
    named_accounts: b.named_accounts ?? undefined,
    all_contract_refs: b.all_contract_refs ?? undefined,
    all_carriers: b.all_carriers ?? undefined,
    booking_ref: bookingRefFromApi(
      commercial.booking_ref ?? b.booking_ref,
      b.source_id,
    ),
    origin_port: toPortInfo(b.origin_port),
    destination_port: toPortInfo(b.destination_port),
    departure_date: b.departure_date ?? undefined,
    arrival_date: b.arrival_date ?? undefined,
    stuffing_date: b.stuffing_date ?? undefined,
    stuffing_date_source: b.stuffing_date_source ?? undefined,
    ready_date: b.ready_date ?? undefined,
    cutoff_date: b.cutoff_date ?? undefined,
    effective_cutoff: b.effective_cutoff ?? undefined,
    cutoff_source: b.cutoff_source ?? undefined,
    effective_pcd: b.effective_pcd ?? undefined,
    pcd_is_etd_fallback: b.pcd_is_etd_fallback || undefined,
    pcd_source: b.pcd_source ?? undefined,
    pricing_date: b.pricing_date ?? undefined,
    pricing_date_is_frozen: b.pricing_date_is_frozen || undefined,
    state_changed_at: b.state_changed_at ?? undefined,
    vessel: b.vessel ?? undefined,
    equipment_summary: b.equipment_summary,
    total_teu: b.total_teu,
    total_containers: b.total_containers,
    load_type: b.load_type ?? undefined,
    weight: b.weight ?? undefined,
    containers: b.containers,
    source_containers: b.source_containers,
    update_source: b.update_source ?? undefined,
    updates: b.updates,
    shipper: b.shipper ?? undefined,
    shipper_name: b.shipper_name ?? undefined,
    consignee: b.consignee ?? undefined,
    consignee_name: b.consignee_name ?? undefined,
    agent: b.agent ?? undefined,
    focus_agent_raw: b.focus_agent_raw ?? undefined,
    active_agent_update_source: b.active_agent_update_source ?? undefined,
    week: b.week ?? undefined,
    service_trade: b.service_trade ?? undefined,
    commodity: b.commodity ?? undefined,
    open_date: b.open_date ?? undefined,
    snapshot_date: b.snapshot_date ?? undefined,
    sales_person: b.sales_person ?? undefined,
    opened_by: openedByFromExtra(b.extra_data),
    pricing_total: matchedBookingPrice(b.pricing_total),
    pricing_currency: b.pricing_currency ?? undefined,
    pricing_matched: b.pricing_matched === true,
    best_saving: b.best_saving ?? undefined,
    first_book_price: b.first_book_price ?? undefined,
    first_book_rec_id: b.first_book_rec_id ?? undefined,
    first_book_carrier: b.first_book_carrier ?? undefined,
    first_book_contract: b.first_book_contract ?? undefined,
    rebook_info: b.rebook_info ?? undefined,
    bl_group_id: b.bl_group_id ?? undefined,
    bl_charge: b.bl_charge ?? undefined,
    operational: b.operational ?? undefined,
    tracking_fetched_at: b.tracking_fetched_at ?? undefined,
    carrier_bl_number: b.carrier_bl_number ?? undefined,
    carrier_bl_source: b.carrier_bl_source ?? undefined,
    extra_data: b.extra_data ?? undefined,
  };
}
